import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_BenchmarkRow,
    WB_BusinessUnitRow,
    WB_BusinessUnitSourceRow,
    WB_SourceRow,
    WB_UserRow,
    WB_UsrBusinessUnitRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbBusinessUnit} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Benchmark,
    esWB_BusinessUnit,
    esWB_BusinessUnit_Source,
    esWB_BusinessUnit_User,
    esWB_Source,
    esWB_User
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {BusinessUnitBOValidation} from "./BusinessUnitBO.Validation";
import {PAEnvironment} from "../PA.Core/PAEnvironment";


export class BusinessUnitBO
  implements IBusinessObject {

 //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_BusinessUnit: WB_BusinessUnitRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Business Unit'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_BusinessUnit = null;
    }

    get WB_BusinessUnit(): WB_BusinessUnitRow {
        return this.drWB_BusinessUnit;
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Business Unit");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{
            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null){
                let sError = "Unable to determine dbContext for new Business Unit";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            //Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_BusinessUnit)
                .then((newBusinessUnitRow: WB_BusinessUnitRow)=>{
                newBusinessUnitRow.setDefaults()
                self.drWB_BusinessUnit = newBusinessUnitRow;
                //add this to all the BOs
                self.dbContext.add(esWB_BusinessUnit, newBusinessUnitRow);
                self.Identity.setAll(self.WB_BusinessUnit.cif.WB_BUSINESSUNIT_ID,self.WB_BusinessUnit.cif.BUSINESSUNIT_ID,self.WB_BusinessUnit.cif.BUSINESSUNIT_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_BusinessUnit,"WB_BUSINESSUNIT_ID","BUSINESSUNIT_ID","BUSINESSUNIT_NAME");

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                resolve(self);
            })
            .catch((err)=>{
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        // if(!this.bIsLoaded)
        //     this.Identity.setAll(anId, anId, "Loading Business Unit (" + anId + ")");

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject)=>{

            if(self.bIsLoaded) {
                let sError = "Business Unit already loaded";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if(this.dbContext == null){
                let sError = "Unable to determine dbContext for new Business Unit";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            //Added for record resolver
            if("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = BusinessUnitBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = BusinessUnitBO.setupRecordResolver(recordResolver,[anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aBusinessUnitRow: any = recordResolver.get(esWB_BusinessUnit, anId);

                    if(_.isEmpty(aBusinessUnitRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_BusinessUnit = aBusinessUnitRow;
                    self.Identity.setAll(self.WB_BusinessUnit.cif.WB_BUSINESSUNIT_ID, self.WB_BusinessUnit.cif.BUSINESSUNIT_ID, self.WB_BusinessUnit.cif.BUSINESSUNIT_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_BusinessUnit, "WB_BUSINESSUNIT_ID", "BUSINESSUNIT_ID", "BUSINESSUNIT_NAME");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function(err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_BusinessUnit.cif.BUSINESSUNIT_NAME + "(" + self.WB_BusinessUnit.cif.BUSINESSUNIT_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject)=>{
            //Does no validations yet
            self.ValidateOptions = options || {};

            if(self.dbContext == null) {
                let sError = "Unable to determine dbContext for to Validate Business Unit";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            BusinessUnitBOValidation.ValidateFields(self)
                .then((ignore)=>{
                    self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbBusinessUnit = <IwbBusinessUnit>self.drWB_BusinessUnit;
        this.SaveProgressProvider.start("Saving " + wbBusinessUnit.BUSINESSUNIT_NAME + "(" + wbBusinessUnit.BUSINESSUNIT_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save BusinessUnit.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if(bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let bDone = false;
            self.Validate({})
                .then((gnore)=>{
                    if(!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }

                    let tasks: Promise<any> [] = [];

                    tasks.push(saveFunction(esWB_BusinessUnit, self.drWB_BusinessUnit, {}));
                    tasks.push(saveFunction(esWB_BusinessUnit_Source, self.drWB_BusinessUnit.WB_BusinessUnitSourceRows));
                    tasks.push(saveFunction(esWB_BusinessUnit_User, self.drWB_BusinessUnit.WB_UsrBusinessUnitRows));
                    tasks.push(saveFunction(esWB_Benchmark, self.drWB_BusinessUnit.WB_BenchmarkRows));
                    return Promise.all(tasks);
                }).then((ignored)=>{
                    if (bDone) return;

                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(()=>{
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
            }).then((ignore)=>{
                self.SaveProgressProvider.finish();
                resolve(self);
            }).catch((err)=>{
                self.SaveProgressProvider.exception(err);
                resolve(self)
            })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbBusinessUnit = <IwbBusinessUnit>self.drWB_BusinessUnit;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbBusinessUnit.BUSINESSUNIT_NAME + "(" + wbBusinessUnit.BUSINESSUNIT_ID + ")")

        _.forEach(self.WB_BusinessUnit.WB_BenchmarkRows, aRow=>{
            aRow.markDeleted()
        })

        _.forEach(self.WB_BusinessUnit.WB_BusinessUnitSourceRows, aRow=>{
            aRow.markDeleted()
        })

        _.forEach(self.WB_BusinessUnit.WB_LicenseRows, aRow=>{
            aRow.markDeleted()
        })

        _.forEach(self.WB_BusinessUnit.WB_LoaderRows, aRow=>{
            aRow.markDeleted()
        })

        _.forEach(self.WB_BusinessUnit.WB_UsrBusinessUnitRows, aRow=>{
            aRow.markDeleted()
        })
        return new Promise<IBusinessObject>((resolve, reject)=>{
            // 1. Validate if we can delete the BusinessUnit

            // 2. If Valid, mark all the records for deletion.
            self.drWB_BusinessUnit.markDeleted();

            if (bSaveNow){
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }


    addSource(aWB_Source_Id: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject) => {
            let aSourceRow: WB_SourceRow = undefined;
            const query: any = { where: { WB_SOURCE_ID: aWB_Source_Id } };

            self.dbContext.findAll(esWB_Source, query)
              .then((aWB_SourceRow: WB_SourceRow[]) => {
                  for(let aSrc of aWB_SourceRow) {
                      aSourceRow = aSrc;
                  }

                  if (!aSourceRow) {
                      return
                  }
                  return self.dbContext.createRecordWithKey(esWB_BusinessUnit_Source)
              })
              .then((aNewRow: WB_BusinessUnitSourceRow) => {
                  if (!aSourceRow) {
                      return
                  }

                  aNewRow.setDefaults();
                  aNewRow.WB_BusinessUnitRow = self.drWB_BusinessUnit
                  aNewRow.cif.WB_BUSINESSUNIT_ID = self.drWB_BusinessUnit.cif.WB_BUSINESSUNIT_ID;
                  aNewRow.cif.WB_SOURCE_ID = aSourceRow.cif.WB_SOURCE_ID
                  aNewRow.WB_SourceRow = aSourceRow
                  self.dbContext.add(esWB_BusinessUnit_Source, aNewRow);
              })
              .then(() => {
                  resolve(self)
              })
              .catch((error) => {
                  reject(error)
              })
        })
    }

    removeSource(aWB_BusinessUnitSR_ID: number): Promise<IBusinessObject>{
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject)=>{
            _.forEach(self.drWB_BusinessUnit.WB_BusinessUnitSourceRows, (aBusinessUnit_SR_ItemRow: WB_BusinessUnitSourceRow)=>{
                if(aBusinessUnit_SR_ItemRow.isActive && aBusinessUnit_SR_ItemRow.cif.WB_BUSINESSUNITSR_ID == aWB_BusinessUnitSR_ID){
                    aBusinessUnit_SR_ItemRow.markDeleted();
                }
            });
            resolve(self)
        })
    }

    addUser(aWB_AuthUsr_Id: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject)=>{
            let aUsrRow: WB_UserRow = undefined;
            const query: any = { where: { WB_AUTHUSR_ID: aWB_AuthUsr_Id}};

            self.dbContext.findAll(esWB_User, query)
                .then((aWB_UsrRow: WB_UserRow[])=>{
                    for(let aUsr of aWB_UsrRow) {
                        aUsrRow = aUsr;
                    }

                    if(!aUsrRow) {
                        return
                    }
                    return self.dbContext.createRecordWithKey(esWB_BusinessUnit_User)
                })
                .then((aNewRow: WB_UsrBusinessUnitRow)=>{
                    if(!aUsrRow) { return }

                    aNewRow.setDefaults();
                    aNewRow.WB_BusinessUnitRow = self.drWB_BusinessUnit;
                    aNewRow.cif.WB_BUSINESSUNIT_ID = self.drWB_BusinessUnit.cif.WB_BUSINESSUNIT_ID;
                    aNewRow.cif.WB_AUTHUSR_ID = aUsrRow.cif.WB_AUTHUSR_ID;
                    aNewRow.WB_UserRow = aUsrRow;
                    self.dbContext.add(esWB_BusinessUnit_User, aNewRow);
                    resolve(self)
                })
                .catch((error)=>{
                    reject(error)
                })
        })
    }

    removeUser(aWB_AuthUsr_Id: number): Promise<IBusinessObject>{
        const self = this;

        return new Promise<IBusinessObject>((resolve, reject)=>{
            _.forEach(self.drWB_BusinessUnit.WB_UsrBusinessUnitRows, (aUsrRow: WB_UsrBusinessUnitRow)=>{
                if(aUsrRow.isActive && aUsrRow.cif.WB_USRBUSINESSUNITR_ID == aWB_AuthUsr_Id){
                    aUsrRow.markDeleted();
                }
            });
            resolve(self)
        })
    }


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const business_unit_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, business_unit_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject)=>{
            recordResolver.findAllWithRelationships(esWB_BusinessUnit, ids)
                .then((result)=>{
                    resolve();
                })
                .catch((error)=>{
                    reject(error);
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( BusinessUnitBO, "BusinessUnitBO" , "Business Unit")